/**
 * Organization Path Utilities
 *
 * Helper functions for building and validating organization-scoped paths
 * Ensures consistent routing structure across the application
 */

/**
 * Valid route segments that should be scoped to an organization
 * These are the main navigation sections of the application
 */
const ORGANIZATION_ROUTE_SEGMENTS = new Set([
  "projects",
  "producers",
  "financial",
  "crm",
  "settings",
  "dashboard",
  "billing-required",
]);

interface SplitPathResult {
  pathname: string;
  search: string;
  hash: string;
}

/**
 * Split a target path into pathname, search, and hash components
 */
function splitTargetPath(target?: string | null): SplitPathResult {
  if (!target || target.trim() === "") {
    return { pathname: "/", search: "", hash: "" };
  }

  let normalized = target.trim();
  let hash = "";

  const hashIndex = normalized.indexOf("#");
  if (hashIndex >= 0) {
    hash = normalized.slice(hashIndex);
    normalized = normalized.slice(0, hashIndex);
  }

  let search = "";
  const searchIndex = normalized.indexOf("?");
  if (searchIndex >= 0) {
    search = normalized.slice(searchIndex);
    normalized = normalized.slice(0, searchIndex);
  }

  if (!normalized.startsWith("/")) {
    normalized = `/${normalized}`;
  }

  return {
    pathname: normalized === "" ? "/" : normalized,
    search,
    hash,
  };
}

/**
 * Ensure a pathname is properly scoped to the organization
 * Handles various input formats and normalizes them
 */
function ensureOrganizationPathname(slug: string, pathname: string): string {
  if (!pathname || pathname === "") {
    return `/${slug}`;
  }

  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;

  // Already correctly scoped
  if (normalized === `/${slug}` || normalized.startsWith(`/${slug}/`)) {
    return normalized;
  }

  const segments = normalized.split("/").filter(Boolean);
  if (segments.length === 0) {
    return `/${slug}`;
  }

  const [first, ...rest] = segments;

  // First segment is a valid organization route
  if (first && ORGANIZATION_ROUTE_SEGMENTS.has(first)) {
    return `/${slug}/${[first, ...rest].join("/")}`;
  }

  // Second segment might be a valid organization route (first was likely a slug)
  if (rest.length > 0 && rest[0] && ORGANIZATION_ROUTE_SEGMENTS.has(rest[0])) {
    return `/${slug}/${rest.join("/")}`;
  }

  // Default to organization home
  return `/${slug}`;
}

/**
 * Build a complete organization-scoped path
 * @param slug Organization slug
 * @param target Target path (can be relative or absolute)
 * @returns Fully qualified organization-scoped path with query params and hash
 */
export function buildOrganizationPath(slug: string, target?: string | null): string {
  const { pathname, search, hash } = splitTargetPath(target);
  const normalizedPathname = ensureOrganizationPathname(slug, pathname);
  return `${normalizedPathname}${search}${hash}`;
}

/**
 * Check if a path is within the given organization's scope
 * @param slug Organization slug
 * @param target Path to check
 * @returns true if path is within organization scope
 */
export function isPathWithinOrganization(slug: string, target?: string | null): boolean {
  const { pathname } = splitTargetPath(target);
  return pathname === `/${slug}` || pathname.startsWith(`/${slug}/`);
}

/**
 * Extract organization slug from a path
 * @param path Path to extract slug from
 * @returns Organization slug or null if not found
 */
export function extractOrganizationSlug(path: string): string | null {
  const { pathname } = splitTargetPath(path);
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) {
    return null;
  }

  const [firstSegment, secondSegment] = segments;
  const candidate = firstSegment === "_authenticated" ? secondSegment : firstSegment;

  // First segment after root should be the organization slug
  // (unless it's a global/public route such as marketing pages under (public))
  const reservedSegments = new Set([
    "sign-in",
    "sign-up",
    "waitlist",
    "app",
    "onboarding",
    "assistant-privacy",
    "admin",
    "pricing",
    "faq",
    "contato",
    "suporte",
    "privacy",
    "terms",
    "noticias",
    "legislacao-agro",
  ]);

  if (candidate && !reservedSegments.has(candidate)) {
    return candidate;
  }

  return null;
}

/**
 * Get the relative path within an organization
 * Removes the organization slug prefix from the path
 * @param slug Organization slug
 * @param path Full path
 * @returns Relative path without organization slug
 */
export function getRelativeOrganizationPath(slug: string, path: string): string {
  const { pathname, search, hash } = splitTargetPath(path);

  if (pathname === `/${slug}`) {
    return `/${search}${hash}`;
  }

  if (pathname.startsWith(`/${slug}/`)) {
    const relative = pathname.slice(slug.length + 1); // +1 for the leading slash
    return `${relative}${search}${hash}`;
  }

  return `${pathname}${search}${hash}`;
}
